import React from 'react';
import { Box, Grid } from '@mui/material';
import { MissionsStore } from '@gamiphy/loyalty-station-desktop/store/mission';
import { observer } from 'mobx-react';
import { round } from '@gamiphy/utils/number';
import { useTranslation } from '@gamiphy/react-i18n';
import { PurchaseStore } from '@gamiphy/loyalty-station-desktop/store/purchase';
import { CategoriesTypes, Category, MissionActions } from '@gamiphy/bl/mission';
import MissionContent from './MissionContent';
import CategoryContent from './CategoryContent';
import CashbackMission from './CashbackMission';
import BirthdayPopup from './BirthdayPopup';
import ParallelCategoryPopup from './ParallelCategoryPopup';

const Missions = observer(() => {
  const { t } = useTranslation();

  const currentCashBack = round(PurchaseStore.currentTierCash, 2);
  const [categoryPopupopen, setCategoryPopupOpen] = React.useState(false);
  const { birthdayMissionPopupOpen } = MissionsStore;
  const [selectedParallelCategory, setSelectedParallelCategory] = React.useState<Category>();

  /* Two refs were used to acheive the scroll because scrollIntoView() method
  causes a scroll on the entire page, which is an undesired effect. */

  const lastItemRef = React.useRef<HTMLDivElement | null>(null);

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (containerRef.current && lastItemRef.current) {
      containerRef.current.scroll({
        behavior: 'smooth',
        top: lastItemRef.current.offsetTop - containerRef.current.offsetTop,
      });
    }
  }, [lastItemRef.current, containerRef.current]);

  const birthdayMission = React.useMemo(() => MissionsStore.userMissions.find(
    (userMission) => userMission.mission.cta?.action === MissionActions.openDatePickerAndSetBirthDate,
  ), [MissionsStore.userMissions]);

  return (
    <Box>
      <Grid
        ref={containerRef}
        container
        direction="column"
        wrap="nowrap"
        gap={2}
        sx={{
          maxHeight: '300px',
          overflowY: 'scroll',
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-track': {
            margin: 1,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#00000066',
            borderRadius: '50px',
          },
          '& >:not(:last-child)': {
            position: 'relative',
            '&::after': {
              content: '""',
              background: 'rgba(0, 0, 0, 0.12)',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              margin: 'auto',
              height: '1px',
              width: '80%',
            },
          },
        }}
      >
        {MissionsStore.completedSequentialMissions.map((userMission, index, arr) => (
          <Grid ref={index + 1 === arr.length ? lastItemRef : null} item key={userMission.id}>
            <MissionContent userMission={userMission} />
          </Grid>
        ))}
        {MissionsStore.completedParallelCategories.map((category) => {
          const categoryMissions = MissionsStore.getMissionsByCategory(category);
          return !!categoryMissions.length && (
          <Grid item key={category.id}>
            {/* If the category only has one mission, show that mission else show the popup  */}
            {categoryMissions.length > 1 ? (
              <CategoryContent
                open={categoryPopupopen}
                setCategoryPopupOpen={setCategoryPopupOpen}
                category={category}
                setSelectedParallelCategory={setSelectedParallelCategory}
              />
            ) : (
              <MissionContent userMission={MissionsStore.getMissionsByCategory(category)[0]} />
            )}
          </Grid>
          );
        })}
        {currentCashBack > 0 && (
        <Grid item>
          <CashbackMission currentCashBack={currentCashBack} />
        </Grid>
        )}
        {MissionsStore.activeCategories.map((category) => {
          if (category.type === CategoriesTypes.parallel) {
            const categoryMissions = MissionsStore.getMissionsByCategory(category);
            return (
              categoryMissions.length > 1 ? (
                <Grid item>
                  <CategoryContent
                    open={categoryPopupopen}
                    setCategoryPopupOpen={setCategoryPopupOpen}
                    category={category}
                    setSelectedParallelCategory={setSelectedParallelCategory}
                  />
                </Grid>
              ) : (
              // Do not render anything if the category has no missions
                !!categoryMissions.length && (
                <Grid item>
                  <MissionContent userMission={MissionsStore.getMissionsByCategory(category)[0]} />
                </Grid>
                )
              )
            );
          }
          return (
            MissionsStore.activeMissionsPerCategory(category).map((mission) => (
              <Grid item>
                <MissionContent userMission={mission} />
              </Grid>
            ))
          );
        })}
        <BirthdayPopup
          userMission={birthdayMission}
          open={birthdayMissionPopupOpen}
          onClose={() => MissionsStore.setBirthdayMissionPopupOpen(false)}
          title={t('birthday.popup.reward', 'Birthday reward')}
        />
        <ParallelCategoryPopup
          category={selectedParallelCategory}
          open={categoryPopupopen}
          onClose={() => setCategoryPopupOpen(false)}
        />
      </Grid>
    </Box>
  );
});

export default Missions;
